import { createSlice } from "@reduxjs/toolkit";
import {
  MonitoringConfig,
  ProfessionalMonitoring,
  WarningPopup,
  CustomerReport,
  getMonitoringConfig,
  getAllMonitoringConfigs,
  updateMonitoringConfig,
  getMonitoringData,
  getAllMonitoringData,
  trackActivity,
  getPendingWarnings,
  getAllWarnings,
  dismissWarning,
  createReport,
  getReportsByProfessional,
  getReportsByClient,
  getAllReports,
  reviewReport,
} from "../Actions/ProfessionalMonitoringActions";

interface ProfessionalMonitoringState {
  configs: MonitoringConfig[];
  selectedConfig: MonitoringConfig | null;
  monitoringData: ProfessionalMonitoring | null;
  allMonitoringData: ProfessionalMonitoring[];
  monitoringPagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;
  pendingWarnings: WarningPopup[];
  allWarnings: WarningPopup[];
  reports: CustomerReport[];
  reportsPagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;
  selectedReport: CustomerReport | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProfessionalMonitoringState = {
  configs: [],
  selectedConfig: null,
  monitoringData: null,
  allMonitoringData: [],
  monitoringPagination: null,
  pendingWarnings: [],
  allWarnings: [],
  reports: [],
  reportsPagination: null,
  selectedReport: null,
  loading: false,
  error: null,
};

const professionalMonitoringSlice = createSlice({
  name: "professionalMonitoring",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMonitoringData: (state) => {
      state.monitoringData = null;
    },
    clearWarnings: (state) => {
      state.pendingWarnings = [];
      state.allWarnings = [];
    },
    clearReports: (state) => {
      state.reports = [];
    },
  },
  extraReducers: (builder) => {
    // Get Monitoring Config
    builder
      .addCase(getMonitoringConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMonitoringConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedConfig = action.payload;
      })
      .addCase(getMonitoringConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get All Monitoring Configs
    builder
      .addCase(getAllMonitoringConfigs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllMonitoringConfigs.fulfilled, (state, action) => {
        state.loading = false;
        state.configs = action.payload;
      })
      .addCase(getAllMonitoringConfigs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update Monitoring Config
    builder
      .addCase(updateMonitoringConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMonitoringConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedConfig = action.payload;
        // Update in configs array
        const index = state.configs.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.configs[index] = action.payload;
        }
      })
      .addCase(updateMonitoringConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Monitoring Data
    builder
      .addCase(getMonitoringData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMonitoringData.fulfilled, (state, action) => {
        state.loading = false;
        state.monitoringData = action.payload;
      })
      .addCase(getMonitoringData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get All Monitoring Data
    builder
      .addCase(getAllMonitoringData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllMonitoringData.fulfilled, (state, action) => {
        state.loading = false;
        state.allMonitoringData = action.payload.data;
        state.monitoringPagination = action.payload.pagination;
      })
      .addCase(getAllMonitoringData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Track Activity
    builder
      .addCase(trackActivity.fulfilled, (state, action) => {
        state.monitoringData = action.payload;
      })
      .addCase(trackActivity.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Get Pending Warnings
    builder
      .addCase(getPendingWarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPendingWarnings.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingWarnings = action.payload;
      })
      .addCase(getPendingWarnings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get All Warnings
    builder
      .addCase(getAllWarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllWarnings.fulfilled, (state, action) => {
        state.loading = false;
        state.allWarnings = action.payload;
      })
      .addCase(getAllWarnings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Dismiss Warning
    builder
      .addCase(dismissWarning.fulfilled, (state, action) => {
        // Remove from pending warnings
        state.pendingWarnings = state.pendingWarnings.filter(
          (w) => w.id !== action.payload.id
        );
        // Update in all warnings
        const index = state.allWarnings.findIndex((w) => w.id === action.payload.id);
        if (index !== -1) {
          state.allWarnings[index] = action.payload;
        }
      })
      .addCase(dismissWarning.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Create Report
    builder
      .addCase(createReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = [action.payload, ...state.reports];
      })
      .addCase(createReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Reports by Professional
    builder
      .addCase(getReportsByProfessional.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReportsByProfessional.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload;
      })
      .addCase(getReportsByProfessional.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Reports by Client
    builder
      .addCase(getReportsByClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getReportsByClient.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload;
      })
      .addCase(getReportsByClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get All Reports
    builder
      .addCase(getAllReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.data;
        state.reportsPagination = action.payload.pagination;
      })
      .addCase(getAllReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Review Report
    builder
      .addCase(reviewReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reviewReport.fulfilled, (state, action) => {
        state.loading = false;
        // Update in reports array
        const index = state.reports.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.reports[index] = action.payload;
        }
      })
      .addCase(reviewReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearMonitoringData, clearWarnings, clearReports } =
  professionalMonitoringSlice.actions;
export default professionalMonitoringSlice.reducer;

